import {useState} from 'react';
import {isAxiosError} from 'axios';
import {useQuery} from '@tanstack/react-query';
import {Plus} from 'lucide-react';
import {
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  FieldError,
  Input,
  Spinner
} from '@kroll-resolver-crisp/ui-components-react';
import {useChannelLatestVideos} from '@/api/hooks';
import {videosApi, type VideoStatus} from '@/api/videos';
import {
  ExampleChip,
  ExampleGridLabel,
  ExampleTile,
  ExampleTileGrid
} from '@/components/classify/ExampleTile';

const VIDEO_ID = /^[\w-]{11}$/;
const URL_ID = /(?:[&?]v=|youtu\.be\/|\/(?:shorts|embed|live)\/)([\w-]{11})/;

/** Pulls an 11-character video id out of a bare id or a watch/shorts/embed/youtu.be URL. */
export function parseVideoId(input: string): string | null {
  const text = input.trim();
  return VIDEO_ID.test(text) ? text : (URL_ID.exec(text)?.[1] ?? null);
}

/** The stored value is a newline-joined string; tolerate commas and stray whitespace from older free text. */
export function splitIds(value: string): string[] {
  return value.split(/[\s,]+/).filter(Boolean);
}

type Props = {
  readonly channelId: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
};

export function ExampleVideoPicker({channelId, value, onChange}: Props) {
  const ids = splitIds(value);
  const latest = useChannelLatestVideos(channelId);
  const [added, setAdded] = useState<Record<string, VideoStatus>>({});
  const [entry, setEntry] = useState('');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const known = new Map<string, VideoStatus>(Object.entries(added));
  for (const video of latest.data?.results ?? []) {
    known.set(video.id, video);
  }

  // Ids that arrived by prefill (resubmit, older free text) and aren't in the latest list or a manual add.
  const unresolved = ids.filter((id) => !known.has(id));
  const resolved = useQuery({
    queryKey: ['videos', 'statuses', unresolved.join(',')],
    queryFn: async () => videosApi.statuses(unresolved),
    enabled: unresolved.length > 0,
    staleTime: 5 * 60 * 1000
  });
  for (const video of resolved.data?.results ?? []) {
    known.set(video.id, video);
  }

  const setIds = (next: string[]) => {
    onChange(next.join('\n'));
  };

  const toggle = (id: string) => {
    setIds(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  };

  const addEntry = async (text: string) => {
    const parsed = splitIds(text).map((part) => ({part, id: parseVideoId(part)}));
    if (parsed.length === 0) {
      return;
    }

    const bad = parsed.find((p) => p.id === null);
    if (bad) {
      setError(`"${bad.part}" is not a video ID or YouTube video link`);
      return;
    }

    setChecking(true);
    setError(null);
    const next = [...ids];
    const found: Record<string, VideoStatus> = {};
    try {
      for (const id of new Set(parsed.map((p) => p.id!))) {
        if (next.includes(id)) {
          continue;
        }

        try {
          found[id] = await videosApi.status(id);
          next.push(id);
        } catch (error_) {
          setError(
            isAxiosError(error_) && error_.response?.status === 404
              ? `Video ${id} was not found on YouTube`
              : `Couldn't verify ${id} right now — try again`
          );
          break;
        }
      }
    } finally {
      // Keep what validated even if a later id in a pasted batch failed.
      setAdded((current) => ({...current, ...found}));
      setIds(next);
      setChecking(false);
    }

    if (next.length === ids.length + Object.keys(found).length) {
      setEntry('');
    }
  };

  return (
    <div className="space-y-3">
      {ids.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {ids.map((id) => {
            const video = known.get(id);
            return (
              <ExampleChip
                key={id}
                title={video?.title ?? id}
                thumbnailUrl={video?.thumbnail_url}
                thumbnailClassName="h-7 w-12"
                onRemove={() => {
                  toggle(id);
                }}
              />
            );
          })}
        </ul>
      ) : null}

      <div className="space-y-1">
        <div className="flex gap-2">
          <Input
            value={entry}
            placeholder="Paste a video ID or link, then press Enter"
            aria-label="Add example video by ID or link"
            aria-invalid={error ? true : undefined}
            disabled={checking}
            onChange={(event) => {
              setEntry(event.target.value);
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void addEntry(entry);
              }
            }}
            onPaste={(event) => {
              // A pasted id is added straight away, same as clicking Add.
              const pasted = event.clipboardData.getData('text');
              if (splitIds(pasted).length > 0 && splitIds(pasted).every((p) => parseVideoId(p))) {
                event.preventDefault();
                setEntry(pasted);
                void addEntry(pasted);
              }
            }}
          />
          <Button
            type="button"
            variant="secondary"
            disabled={checking || entry.trim() === ''}
            onClick={() => void addEntry(entry)}>
            {checking ? <Spinner /> : <Plus />}
            Add
          </Button>
        </div>
        {error ? <FieldError>{error}</FieldError> : null}
      </div>

      <div className="space-y-1.5">
        <ExampleGridLabel>Latest uploads</ExampleGridLabel>
        {latest.isLoading ? (
          <div className="text-muted-foreground flex items-center gap-2 text-xs">
            <Spinner />
            Loading latest uploads…
          </div>
        ) : latest.isError ? (
          <Empty variant="outline" className="p-4">
            <EmptyHeader>
              <EmptyTitle>Couldn&apos;t load this channel&apos;s videos</EmptyTitle>
              <EmptyDescription>You can still add IDs above.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (latest.data?.results.length ?? 0) === 0 ? (
          <Empty variant="outline" className="p-4">
            <EmptyHeader>
              <EmptyTitle>No videos found</EmptyTitle>
              <EmptyDescription>This channel has no recent uploads to pick from.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <ExampleTileGrid label="Latest uploads">
            {latest.data?.results.map((video) => (
              <ExampleTile
                key={video.id}
                title={video.title ?? video.id}
                thumbnailUrl={video.thumbnail_url}
                selected={ids.includes(video.id)}
                aspect="video"
                onToggle={() => {
                  toggle(video.id);
                }}
              />
            ))}
          </ExampleTileGrid>
        )}
      </div>
    </div>
  );
}
